import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { clientQuery } from 'Core/data/GraphQLApi';
import { endpoints } from 'Core/defaultValues';

const QUERY = `
  query getVidFleetClipById($clipId: String!, $rawData: Boolean!) {
    getVidFleetClipById(clipId: $clipId, rawData: $rawData)
  }
`;

const INIT_STATE = {
  clips: {},
  loading: {},
  error: {},
};

export const fetchWaylensClip = createAsyncThunk(
  'waylensClip/fetch',
  async ({ clipId }, { rejectWithValue }) => {
    try {
      const result = await clientQuery(
        QUERY,
        { clipId: String(clipId), rawData: true },
        endpoints.GRAPHQL_GENERAL_V2
      );

      const raw = result?.getVidFleetClipById;
      if (!raw) throw new Error('No clip data returned');

      const clip = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return { clipId, clip };
    } catch (exc) {
      return rejectWithValue({ clipId, error: exc?.message || 'Failed to fetch clip' });
    }
  },
  {
    condition: ({ clipId }, { getState }) => {
      const { waylensClipRedux } = getState();
      if (waylensClipRedux?.loading?.[clipId]) return false;
      if (waylensClipRedux?.clips?.[clipId]) return false;
      return true;
    },
  }
);

const waylensClipSlice = createSlice({
  name: 'waylensClip',
  initialState: INIT_STATE,
  reducers: {
    clearClip(state, action) {
      const { clipId } = action.payload;
      delete state.clips[clipId];
      delete state.loading[clipId];
      delete state.error[clipId];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchWaylensClip.pending, (state, action) => {
      const { clipId } = action.meta.arg;
      state.loading[clipId] = true;
      state.error[clipId] = null;
    });

    builder.addCase(fetchWaylensClip.fulfilled, (state, action) => {
      const { clipId, clip } = action.payload;
      state.clips[clipId] = clip;
      state.loading[clipId] = false;
    });

    builder.addCase(fetchWaylensClip.rejected, (state, action) => {
      const { clipId } = action.payload ?? action.meta.arg;
      state.loading[clipId] = false;
      state.error[clipId] = action.payload?.error ?? 'Unknown error';
    });
  },
});

export const { actions, reducer } = waylensClipSlice;
export const { clearClip } = actions;
export default reducer;
